import React from 'react';

import {
    ORGANIGRAM_BEHIND_SCHEDULE_COLOUR,
    ORGANIGRAM_COMMITTED_COLOUR,
    ORGANIGRAM_TAG_COLOURS,
    POSITION_TYPE_ORDER,
    TYPE_TO_FILTER_KEY,
    TYPE_TO_ORGANIGRAM_TAG,
    positionTypeLabel,
} from 'Components/people/positionTypes';

/**
 * WorkforceTypeFilterPanel
 *
 * Renders one checkbox per position type (paid / secondee / consultant /
 * volunteer) with a colour-coded swatch, and a read-only key for the committed
 * and behind-schedule indicators. Disables the last ticked checkbox to prevent
 * an empty chart.
 *
 * The swatches are miniature replicas of the org chart's own cards — a white
 * box with a left accent bar — and read their colours from the same
 * `ORGANIGRAM_TAG_COLOURS` map the node templates do, so a swatch cannot drift
 * away from the nodes it explains.
 *
 * Props:
 *   filterState  — { paid: bool, secondee: bool, consultant: bool, volunteer: bool }
 *   onChange     — (newState) => void
 */
export default function WorkforceTypeFilterPanel({ filterState, onChange }) {
    const tickedCount = Object.values(filterState).filter(Boolean).length;

    function handleChange(key) {
        onChange({ ...filterState, [key]: !filterState[key] });
    }

    return (
        <div className="tw-flex tw-flex-row tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 tw-p-2 tw-mb-2">
            {POSITION_TYPE_ORDER.map((type) => {
                const key = TYPE_TO_FILTER_KEY[type];
                const checked = filterState[key] !== false;
                // Disable the checkbox if it's the only one still ticked
                const disabled = checked && tickedCount === 1;

                return (
                    <label
                        key={key}
                        className={`tw-flex tw-items-center tw-gap-x-2 tw-cursor-pointer tw-select-none ${disabled ? 'tw-opacity-50 tw-cursor-not-allowed' : ''}`}
                    >
                        <input
                            type="checkbox"
                            className="tw-cursor-pointer"
                            checked={checked}
                            disabled={disabled}
                            onChange={() => !disabled && handleChange(key)}
                        />
                        <span
                            className="tw-inline-block tw-rounded tw-border tw-border-solid tw-border-charcoal-25 tw-bg-white"
                            style={{
                                width: 22,
                                height: 18,
                                borderInlineStartWidth: 5,
                                borderInlineStartColor: ORGANIGRAM_TAG_COLOURS[TYPE_TO_ORGANIGRAM_TAG[type]],
                                flexShrink: 0,
                            }}
                        />
                        <span className="tw-text-sm tw-text-gray-700">{positionTypeLabel(type)}</span>
                    </label>
                );
            })}

            {/* Not filters — the key for the two marks a card can carry. Each is a
                miniature of the mark itself, drawn from the same colour source. */}
            <span className="tw-flex tw-items-center tw-gap-x-2">
                <span className="tw-inline-block tw-rounded-full" style={{ width: 8, height: 8, background: ORGANIGRAM_COMMITTED_COLOUR, flexShrink: 0 }} />
                <span className="tw-text-sm tw-text-gray-700">{$t('Committed')}</span>
            </span>

            <span className="tw-flex tw-items-center tw-gap-x-2">
                <span
                    style={{
                        width: 0,
                        height: 0,
                        borderInlineStart: '5px solid transparent',
                        borderInlineEnd: '5px solid transparent',
                        borderBottom: `9px solid ${ORGANIGRAM_BEHIND_SCHEDULE_COLOUR}`,
                        flexShrink: 0,
                    }}
                />
                <span className="tw-text-sm tw-text-gray-700">{$t('Behind schedule')}</span>
            </span>
        </div>
    );
}
